/**
 * contenteditable HTML editor interop.
 * One instance per editor, registered by id; .NET drives commands and receives
 * input notifications. Commands use document.execCommand (formatBlock for
 * headings) — no external dependencies.
 */

'use strict';

interface EditorInstance {
    element: HTMLElement;
    dotnet: DotNetObjectReference;
    observer: MutationObserver;
    timer: ReturnType<typeof setTimeout> | null;
    /** AbortController wiring the focusout flush listener; aborted on dispose. */
    controller: AbortController;
}

import type { DotNetObjectReference } from '../types.js';
import { safeInvokeSync } from '../utils/dotnet.js';

const editorRegistry = new Map<string, EditorInstance>();

function generateUniqueId(): string {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
    } catch {
        // Fall through to the timestamp-based id
    }
    return `${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

/**
 * Debounced .NET notification. A MutationObserver fires for every keystroke over
 * potentially large documents, so pushes to .NET are coalesced into one per 150 ms.
 */
function notify(entry: EditorInstance): void {
    if (entry.timer) clearTimeout(entry.timer);
    entry.timer = setTimeout(() => safeInvokeSync(entry.dotnet, 'OnHtmlChanged', entry.element.innerHTML), 150);
}

/**
 * Push the surface's HTML to .NET immediately, cancelling any pending debounced
 * notification. Leaving the field flushes synchronously so a value is never
 * stranded on the surface when the next DOM gesture (e.g. clicking a form's
 * submit button) is the very next event — the debounced push would land after
 * that submit's validation had already read an empty value.
 */
function flush(entry: EditorInstance): void {
    if (entry.timer) {
        clearTimeout(entry.timer);
        entry.timer = null;
    }
    safeInvokeSync(entry.dotnet, 'OnHtmlChanged', entry.element.innerHTML);
}

/**
 * Execute a document.execCommand against the editor's document and notify .NET.
 *
 * @param id - Editor instance ID
 * @param command - execCommand name (e.g. 'bold', 'justifyLeft', 'formatBlock')
 * @param value - Optional command value (e.g. '<h1>' for formatBlock)
 */
export function exec(id: string, command: string, value?: string): void {
    const entry = editorRegistry.get(id);
    if (!entry) return;
    entry.element.focus();
    document.execCommand(command, false, value);
    notify(entry);
}

/**
 * Get the current HTML of an editor instance.
 *
 * @param id - Editor instance ID
 * @returns The editor's innerHTML (empty string if unknown id)
 */
export function getHtml(id: string): string {
    return editorRegistry.get(id)?.element.innerHTML ?? '';
}

/**
 * Replace the editor's HTML and notify .NET immediately (programmatic set is a
 * deliberate sync point, unlike the debounced user-edit notifications).
 *
 * @param id - Editor instance ID
 * @param html - The HTML to set
 */
export function setHtml(id: string, html: string): void {
    const entry = editorRegistry.get(id);
    if (!entry) return;
    entry.element.innerHTML = html;
    notify(entry);
}

/**
 * Focus the editor surface.
 *
 * @param id - Editor instance ID
 */
export function focus(id: string): void {
    editorRegistry.get(id)?.element.focus();
}

/**
 * Register a contenteditable element as an editor instance and observe its
 * content for changes.
 *
 * @param element - The contenteditable element (required)
 * @param dotnet - .NET object reference receiving OnHtmlChanged callbacks
 * @returns The instance ID to address later calls with
 */
export function initialize(element: HTMLElement | null, dotnet: DotNetObjectReference): string {
    if (!element) {
        throw new Error('HtmlEditor initialization failed: element is required');
    }
    const controller = new AbortController();
    const id = generateUniqueId();
    const instance: EditorInstance = { element, dotnet, observer: new MutationObserver(() => notify(instance)), timer: null, controller };
    editorRegistry.set(id, instance);
    // Observe childList/character changes so any editing path (typing, paste,
    // execCommand) reaches .NET; 'input' alone misses execCommand-driven DOM swaps.
    instance.observer.observe(element, { childList: true, characterData: true, subtree: true });
    // Focus leaving the surface flushes the pending HTML at once — before the
    // click that took focus can also submit the surrounding form.
    element.addEventListener('focusout', () => flush(instance), { signal: controller.signal });
    return id;
}

/**
 * Dispose an editor instance: cancel any pending debounced notification,
 * disconnect the observer, and drop the registry entry. The .NET side disposes
 * its own DotNetObjectReference (the JS surface has no dispose method).
 *
 * @param id - Editor instance ID
 */
export function dispose(id: string): void {
    const entry = editorRegistry.get(id);
    if (!entry) return;
    try {
        if (entry.timer) clearTimeout(entry.timer);
        entry.observer.disconnect();
        entry.controller.abort();
    } catch {
        // Cleanup errors are non-fatal
    }
    editorRegistry.delete(id);
}
